import React, { Component } from 'react'
import { Link, withRouter } from 'react-router-dom'
import { extendObservable, computed } from 'mobx'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import copy from 'copy-to-clipboard'
import moment from 'moment'
import _ from 'lodash'

import {
  addQueryParams,
  downloadFromUrl,
  tokenStorage,
  exportDialogMessage,
  sendMetric
} from '@atheneum/api/dist/lib'

import { eplApi, projectApi } from '@atheneum/api'

import Action from '@components/Action'
import { showDialog, isCancellationError } from '@components/Dialog'
import AddExpertIcon from '@components/Icon/AddExpert'
import ArrowBack from '@components/Icon/ArrowBack'
import BatchIcon from '@components/Icon/Batch'
import CopyIcon from '@components/Icon/Copy'
import DownloadIcon from '@components/Icon/Download'
import EmailShareIcon from '@components/Icon/EmailShare'
import FilterIcon from '@components/Icon/Filter'
import LinkIcon from '@components/Icon/Link'
import MailIcon from '@components/Icon/Mail'
import Tooltip from '@components/Tooltip'

import checkInvitationErrors from './checkInvitationErrors'

class Actions extends Component {
  constructor(props) {
    super(props)
    extendObservable(this, {
      selectedEpls: computed(() =>
        this.props.eplStore.eplModels.filter(e => e.batchChecked)
      ),
      job: null,
      exportInProgress: false,
      progress: {
        submitting: false,
        total: null,
        valid: null,
        processed: 0,
        failed: 0
      }
    })
  }

  onCopyEPLs = () => {
    const { growlStore } = this.props
    if (this.selectedEpls.length === 0) {
      return growlStore.showError('You should select an expert first.')
    }

    const experts = this.selectedEpls.map(e => e.epl.expert)

    return this.props.modalStore.show({
      type: 'EXPERT_TO_PROJECT_FORM',
      props: {
        experts: _.uniqBy(experts, 'id'),
        isCopy: true
      }
    })
  }

  onSuggestedExperts = () => {
    const {
      CONST: {
        EPL_STATUSES: { SUBMITTED }
      }
    } = this.props.staticData

    const similarTo = this.props.eplStore.eplModels
      .filter(({ epl }) => {
        return epl.eplStatusId >= SUBMITTED
      })
      .map(({ epl: { expert } }) => ({
        value: expert.id,
        label: expert.fullName
      }))

    if (similarTo.length === 0) {
      showDialog({
        title: 'No suggested experts found',
        text:
          'You need to submit an expert first in order to receive suggestions.',
        type: 'error'
      })
    } else {
      this.props.history.push({
        pathname: '/expert-search',
        state: { similarTo }
      })
    }
  }

  onGetLinkClick = async () => {
    const { project, growlStore } = this.props

    try {
      // replace process.env.REACT_APP_CLIENT_AUTH === 'false'
      // with check for project.cascadedSettings
      if(process.env.REACT_APP_CLIENT_AUTH === 'false') {
        const { externLink } = await projectApi.getExternLink(project.id)

        copy(externLink)
        return showDialog({
          title: 'URL for the EPL has been copied to your clipboard',
          text: externLink,
          type: 'success'
        })
      }

      const externLinks = await projectApi.getExternLinks(project.id)

      if (externLinks?.length) {
        return this.props.modalStore.show({
          type: 'CLIENT_PROJECT_LINK',
          props: {
            externLinks,
            hideOkButton: true,
            hideCancelButton: true,
            showCloseIcon: true,
            projectId: project.id,
            projectName: project.projectName
          }
        })
      } else {
        const { externLink } = await projectApi.getExternLink(project.id)
        copy(externLink)
        return showDialog({
          title: 'Link for Client Interface has been copied to your clipboard',
          text: externLink,
          type: 'success'
        }).catch(err => {
          if (isCancellationError(err)) return
          growlStore.showError(err)
        })
      }
    } catch (error) {
      return showDialog({
        title: 'Project is closed',
        text: 'You need to open the project to be able to generate the link.',
        type: 'error'
      }).catch(err => isCancellationError(err))
    }
  }

  handleEplsExport = () => this.onDownloadClick()

  onDownloadClick = async () => {
    if (this.job && this.exportInProgress) return
    const { projectStore, queueProcessorStore } = this.props

    const projectId = projectStore.project.id
    const projectName = projectStore.project.projectName

    return this.props.modalStore.show({
      type: 'EPL_EXPORT_MODAL',
      props: {
        noSelectedEpls: this.selectedEpls.length,
        onCancel: () => this.props.modalStore.close(),
        onOk: async (exportAll, format) => {
          const { jobId } = await projectApi.exportProjectPipeline(
            projectId,
            exportAll ? null : this.selectedEpls.map(e => e.id),
            format
          )

          const filename = `${projectName}_${moment().format('YYYYMMDD')}.xlsx`

          queueProcessorStore.trackJob(jobId, ({ url }) =>
            downloadFromUrl({
              url: `${url}?token=${tokenStorage.get()}`,
              filename
            })
          )

          this.job = queueProcessorStore.jobs[jobId]

          if (this.job && this.job.loading) {
            this.exportInProgress = true
            setTimeout(() => {
              this.exportInProgress = false
            }, 1000)
          }
          this.props.modalStore.close()
          await exportDialogMessage({ exportType: 'EPLs' })
        }
      }
    })
  }

  onShareEplsClick = () => {
    const {
      staticData,
      growlStore,
      extractEplStore,
      history,
      eplStore
    } = this.props
    const { SUBMITTED } = staticData.CONST.EPL_STATUSES
    const lowerThanSubmitted = this.selectedEpls.filter(
      selectedEpl => selectedEpl.epl.eplStatusId < SUBMITTED
    )
    if (lowerThanSubmitted.length) {
      return growlStore.showError(
        'One or more experts in a status less than Submitted'
      )
    }
    extractEplStore.selectedTemplateType = null
    extractEplStore.selectedTemplate = null
    extractEplStore.selectedEpls.replace(this.selectedEpls.map(e => e.epl))
    extractEplStore.EPL_STATUSES = staticData.CONST.EPL_STATUSESeplStatuses
    extractEplStore.eplStatuses = eplStore.eplStatuses
    const pathname = history.location.pathname.replace(
      /experts-pipeline.*/g,
      'extract-epls'
    )
    history.push({ pathname })
  }

  renderShareEpls() {
    const { eplModels } = this.props.eplStore

    const eplsExist = eplModels.length > 0
    const eplsSelected = this.selectedEpls.length

    const disabled = !eplsExist || !eplsSelected

    const tooltipMessage = 'You should select an expert first'

    return (
      <Action onClick={this.onShareEplsClick} disabled={disabled}>
        <Tooltip placement="top" tooltip={tooltipMessage} disabled={!disabled}>
          <li>
            <EmailShareIcon />
            <span>Share EPL{eplsSelected > 1  ? 's'  : ''}</span>
          </li>
        </Tooltip>
      </Action>
    )
  }

  onSendInviteClick = () => {
    const {
      project,
      segmentStore,
      eplStore,
      modalStore,
      growlStore,
      staticData
    } = this.props

    if (this.selectedEpls.length === 0) {
      return growlStore.showError('You should select an expert first.')
    }

    const { eligible, errors } = checkInvitationErrors({
      staticData,
      segments: segmentStore.segments,
      selectedEpls: this.selectedEpls
    })

    if (!eligible) {
      return showDialog({
        title: 'Cannot invite experts',
        html: `<div>${errors.join('<br /><br />')}</div>`,
        type: 'error'
      }).catch(err => isCancellationError(err))
    }

    const expertIds = this.selectedEpls.map(({ epl }) => epl.expert.id)
    const projectId = project.id
    // Take any EPL as they all have to be on the same segment

    const segment = segmentStore.segments.find(
      s => s.id === this.selectedEpls[0].epl.segmentId
    )
    const senderIds = segment.employees.map(e => e.id)

    const loadTemplateUrl = this.chunkedLoadTemplateUrls(
      '/api/expert-project-link/expert-invite-template',
      expertIds,
      segment.id
    )
    let failedExperts = ''
    return this.props.modalStore.show({
      type: 'EMAIL_TEMPLATE_SEND',
      props: {
        group: 'expert_invite',
        loadTemplateUrl,
        onSend: async templateData => {
          let epls
            ; ({ epls, failedExperts } = await this.sendEmail(templateData, false))
          return epls
        },
        onAfterSend: epls => {
          modalStore.close()

          const title =
            epls.length > 1
              ? `Experts have been invited to the project`
              : `Expert has been invited to the project`

          return showDialog({
            title,
            type: 'success',
            html: `
             <p>
               ${epls.map(epl => `<br /> ${epl.expert.fullName}`)}
             </p>
             ${failedExperts.length > 0
                ? `<div>
   
                 <div class="swal2-icon swal2-error swal2-icon-show">
   
                   <span class="swal2-x-mark">
   
                     <span class="swal2-x-mark-line-left"></span>
   
                     <span class="swal2-x-mark-line-right"></span>
   
                   </span>
   
                 </div>
   
                 <h2 class="swal2-title">Failed to create EPL's for the following experts</h2>
   
                 <p style="color: #FF5858">${failedExperts}</p>
   
               </div>`
                : ''
              }
           `
          }).then(() => {
            eplStore.getList(projectId)
            eplStore.getFilterOptions(projectId)
          })
        },
        progress: this.progress,
        senderIds,
        useCc: false
      }
    })
  }

  resetProgressBar = () => {
    this.progress.submitting = false
    this.progress.total = null
    this.progress.valid = null
    this.progress.processed = 0
  }

  showProgressBar = (total, valid) => {
    this.progress.submitting = true
    this.progress.total = total
    this.progress.valid = valid
    this.progress.processed = 0
  }

  chunkedLoadTemplateUrls = (apiPath, expertIds, segmentId) => {
    const chunkLimit = 100
    const chunks = Math.ceil(expertIds.length / chunkLimit)
    const loadTemplateUrls = []

    for (let index = 0; index < chunks; index++) {
      const start = index * chunkLimit
      const end = (index + 1) * chunkLimit
      const chunk = expertIds.slice(start, end)

      const data = {
        expertIds: chunk
      }

      if (segmentId) {
        data.segmentId = segmentId
      }

      const chunkTemplateUrl = addQueryParams(apiPath, data)
      loadTemplateUrls.push(chunkTemplateUrl)
    }

    return loadTemplateUrls
  }

  onSendFollowupClick = () => {
    const {
      eplStore,
      segmentStore,
      modalStore,
      growlStore,
      staticData,
      project
    } = this.props

    if (this.selectedEpls.length === 0) {
      return growlStore.showError('You should select an expert first.')
    }

    const { eligible, errors } = checkInvitationErrors({
      staticData,
      segments: segmentStore.segments,
      selectedEpls: this.selectedEpls,
      isFollowUp: true
    })

    if (!eligible) {
      return showDialog({
        title: 'Cannot email experts',
        html: `<div>${errors.join('<br /><br />')}</div>`,
        type: 'error'
      }).catch(err => isCancellationError(err))
    }

    const expertIds = this.selectedEpls.map(({ epl }) => epl.expert.id)

    // Take any EPL as they all have to be on the same segment
    const segment = segmentStore.segments.find(
      s => s.id === this.selectedEpls[0].epl.segmentId
    )
    const senderIds = segment.employees.map(e => e.id)

    const loadTemplateUrl = this.chunkedLoadTemplateUrls(
      '/api/expert-project-link/expert-follow-up-template',
      expertIds
    )
    let failedExperts = ''
    return modalStore.show({
      type: 'EMAIL_TEMPLATE_SEND',
      props: {
        group: 'expert_follow_up',
        loadTemplateUrl,
        onSend: async templateData => {
          let epls
            ; ({ epls, failedExperts } = await this.sendEmail(
              templateData,
              true
            ))
          return epls
        },
        onAfterSend: epls => {
          modalStore.close()

          const title =
            epls.length > 1 ? 'Email sent to experts' : 'Email sent to expert'

          return showDialog({
            title,
            type: 'success',
            html: `
             <p>
               ${epls.map(epl => `<br /> ${epl.expert.fullName}`)}
             </p>
             ${failedExperts.length > 0
                ? `<div>
  
                <div class="swal2-icon swal2-error swal2-icon-show">
  
                  <span class="swal2-x-mark">
  
                    <span class="swal2-x-mark-line-left"></span>
  
                    <span class="swal2-x-mark-line-right"></span>
  
                  </span>
  
                </div>
  
                <h2 class="swal2-title">Failed to create EPL's for the following experts</h2>
  
                <p style="color: #FF5858">${failedExperts}</p>
  
              </div>`
                : ''
              }
           `
          }).then(() => {
            eplStore.getList(project.id)
            eplStore.getFilterOptions(project.id)
          })
        },
        progress: this.progress,
        senderIds,
        useCc: false
      }
    })
  }

  async sendEmail(templateData, isFollowup) {
    let failedExperts = ''
    function wait(delay) {
      return new Promise(resolve => setTimeout(resolve, delay))
    }

    function sendEmailWithRetry(delay, tries, chunk, progress) {
      function onError(err) {
        tries = tries - 1
        if (!tries) {
          progress.failed += chunk.length
          return []
        }
        return wait(delay).then(() =>
          sendEmailWithRetry(delay, tries, chunk, progress)
        )
      }
      if (isFollowup) {
        return projectApi
          .emailExperts({ eplIds: chunk.map(i => i.id), templateData })
          .catch(onError)
      } else {
        return eplApi
          .bulkInvite({ eplIds: chunk.map(i => i.id), templateData })
          .catch(onError)
      }
    }
    this.showProgressBar(this.selectedEpls.length, this.selectedEpls.length)
    let epls = []
    let failedInvites = []
    for (let i = 0; i < this.selectedEpls.length; i += 20) {
      let triesLeft = 2
      const chunk = this.selectedEpls.slice(i, i + 20)
      const res = await sendEmailWithRetry(
        500,
        triesLeft,
        chunk,
        this.progress
      )
      const inviteEpls = res.success
      const failed = res.failed
      this.progress.processed += chunk.length
      if (inviteEpls && inviteEpls.length) {
        epls = [...epls, ...inviteEpls]
      }
      if (failed && failed.length) {
        failedInvites = [...failedInvites, ...failed]
      }
    }
    if (failedInvites.length) {
      failedExperts = failedInvites
        .map(selectedEpl => selectedEpl.expert.fullName)
        .join(',<br />')
    }

    this.resetProgressBar()
    return { epls, failedExperts }
  }

  renderExportLink() {
    const { eplModels } = this.props.eplStore

    const eplsExist = eplModels.length > 0
    const eplsAreSelected = this.selectedEpls.length

    const tooltipMessage =
      !eplsExist && !eplsAreSelected
        ? 'There are no expert profiles to export'
        : 'Export EPLs as Excel spreadsheet'

    return (
      <Action
        onClick={this.handleEplsExport}
        disabled={!eplsExist && !eplsAreSelected}>
        <Tooltip placement="top" tooltip={tooltipMessage}>
          <li>
            <DownloadIcon />
            <span>Export</span>
          </li>
        </Tooltip>
      </Action>
    )
  }

  render() {
    const { match, eplStore, projectStore } = this.props
    const {
      doShowGroupsAndFilters,
      doShowBatchActions,
      showBatchActions,
      showGroupsAndFilters
    } = eplStore

    return (
      <>
        <div className="actions-wrapper actions-wrapper--space-between">
          <ul className="actions">
            {match && match.params.eplId ? (
              <Action>
                <Link
                  to={`/project-search/project/${projectStore.id}/experts-pipeline`}>
                  <li>
                    <ArrowBack />
                    <span>Back to list</span>
                  </li>
                </Link>
              </Action>
            ) : null}
            <Action onClick={this.onSuggestedExperts}>
              <li>
                <AddExpertIcon />
                <span>Suggested Experts</span>
              </li>
            </Action>
            <Action onClick={this.onCopyEPLs}>
              <li>
                <CopyIcon />
                <span>Copy experts</span>
              </li>
            </Action>
          </ul>
          <ul className="actions">
            <Action>
              <li onClick={this.onSendInviteClick}>
                <MailIcon />
                <span>Send invite</span>
              </li>
            </Action>
            <Action>
              <li onClick={this.onSendFollowupClick}>
                <MailIcon />
                <span>Send follow up</span>
              </li>
            </Action>
            <Action onClick={this.onGetLinkClick}>
              <li>
                <LinkIcon />
                <span>Get link</span>
              </li>
            </Action>
            {process.env.REACT_APP_FEATURE_EPL_EXTRACTS &&
              this.renderShareEpls()}
            {this.renderExportLink()}
          </ul>
        </div>
        <div className="actions-wrapper actions-wrapper--space-between">
          <ul className="actions">
            {!match || !match.params.eplId ? (
              <Action onClick={showGroupsAndFilters}>
                <li
                  className={doShowGroupsAndFilters ? 'actions__active' : null}>
                  <FilterIcon
                    className={doShowGroupsAndFilters ? 'icon__active' : null}
                  />
                  <span>Group & Filter</span>
                </li>
              </Action>
            ) : null}

            {!match || !match.params.eplId ? (
              <Action onClick={showBatchActions}>
                <li className={doShowBatchActions ? 'actions__active' : null}>
                  <BatchIcon
                    className={doShowBatchActions ? 'icon__active' : null}
                  />
                  <span>Batch actions</span>
                </li>
              </Action>
            ) : null}
          </ul>
        </div>
      </>
    )
  }

  static propTypes = {
    growlStore: PropTypes.object,
    projectStore: PropTypes.object,
    queueProcessorStore: PropTypes.object,
    match: PropTypes.object,
    eplStore: PropTypes.object,
    segmentStore: PropTypes.object,
    extractEplStore: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    project: PropTypes.object,
    history: PropTypes.object
  }
}

export default inject(
  'growlStore',
  'eplStore',
  'projectStore',
  'segmentStore',
  'modalStore',
  'staticData',
  'queueProcessorStore',
  'extractEplStore'
)(withRouter(observer(Actions)))
